import { useEffect, useState } from "react";
import { FaBars, FaPlus, FaHome } from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";
import styles from "./Sidebar.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import us30Image from "../../assets/images/us30.png";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";
import chatStore from "../../store/chatStore";
import BadgeCount from "../ChatPanel/BadgeCount";

// Import DND Kit
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableCommunityItem = ({
  community,
  index,
  active,
  onClick,
  unreadCount,
  isReorderMode,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: community.id,
    disabled: !isReorderMode,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isReorderMode ? "grab" : "pointer",
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.badgeWrapper}>
      <div
        className={`${styles.sidebarItem} ${active ? styles.active : ""}`}
        onClick={!isReorderMode ? onClick : undefined}
        {...(isReorderMode ? { ...attributes, ...listeners } : {})}
      >
        <div className={styles.indicator} />
        <img
          src={
            community.is_us30
              ? us30Image
              : `${ENDPOINTS.IMAGE_BASE_URL}${community.icon}`
          }
          alt="icon"
          className={styles.iconImage}
          draggable={false}
        />
        {isReorderMode && (
          <div className={styles.dragHandle}>
            <MdDragIndicator />
          </div>
        )}
      </div>
      {unreadCount > 0 && !isReorderMode && (
        <div className={styles.badge}>
          <BadgeCount count={unreadCount} />
        </div>
      )}
    </div>
  );
};

const SidebarItem = ({ active, onClick, children, isImage }) => (
  <div
    className={`${styles.sidebarItem} ${active ? styles.active : ""}`}
    onClick={onClick}
  >
    <div className={styles.indicator} />
    {isImage ? (
      <img src={children} alt="icon" className={styles.iconImage} />
    ) : (
      <div className={styles.iconBtn}>{children}</div>
    )}
  </div>
);

// Shimmer skeleton component
const CommunitySkeleton = () => (
  <div className={styles.badgeWrapper}>
    <div className={styles.sidebarItem}>
      <div className={styles.indicator} />
      <div className={`${styles.iconImage} ${styles.shimmer}`} />
    </div>
  </div>
);

const Sidebar = () => {
  const { user, userMeta } = useAuthStore();
  const { unreadChatCounts } = chatStore();
  const {
    isHomeScreen,
    isIconChoosed,
    isUS30Choosed,
    communityIndex,
    communities,
    setIsHomeScreen,
    setIsIconChoosed,
    setIsUS30Choosed,
    setCommunityIndex,
    setCommunities,
    allChannels,
    setAllChannels,
    setShowStoriesViewer,
    viewedStoryIds,
    initViewedStoryIds,
  } = useDashboardStore();

  const [debouncedCounts, setDebouncedCounts] = useState({});
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [sidebarStories, setSidebarStories] = useState([]);

  const canViewStory = (story) => {
    const role = user?.roles?.[0];
    if (role === "administrator") return true;
    if (!story.plan_ids || story.plan_ids.length === 0) return true;
    if (userMeta?.has_active_iap === true) return true;

    let userMembershipIds = [];
    if (Array.isArray(userMeta?.membership_ids)) {
      userMembershipIds = userMeta.membership_ids.map(Number);
    } else if (typeof userMeta?.membership_ids === "string") {
      try {
        userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
      } catch {
        userMembershipIds = [];
      }
    }

    const paidIds = story.plan_ids.map(Number).filter((id) => id > 0);
    return paidIds.some((id) => userMembershipIds.includes(id));
  };

  // Fetch stories on mount to initialise the border state
  useEffect(() => {
    api.get(ENDPOINTS.GET_ALL_STORIES).then(({ data }) => {
      if (data.success) {
        const sorted = (data.stories || []).sort((a, b) => a.sort_order - b.sort_order);
        setSidebarStories(sorted);
        initViewedStoryIds(sorted.filter((s) => s.viewed).map((s) => s.id));
      }
    }).catch(() => {});
  }, []);

  const allViewed =
    sidebarStories.length > 0 &&
    sidebarStories.every((s) => viewedStoryIds.includes(s.id));

  const handleStoriesClick = async () => {
    if (storiesLoading) return;
    setStoriesLoading(true);
    try {
      const { data } = await api.get(ENDPOINTS.GET_ALL_STORIES);
      if (data.success) {
        const sorted = (data.stories || []).sort(
          (a, b) => a.sort_order - b.sort_order
        );
        // Update local border state with fresh data
        setSidebarStories(sorted);
        initViewedStoryIds(sorted.filter((s) => s.viewed).map((s) => s.id));

        const accessible = sorted.filter(canViewStory);
        if (accessible.length === 0) {
          toast.info("No stories available right now");
        } else {
          const firstUnviewedIdx = accessible.findIndex((s) => !s.viewed);
          const startIndex = firstUnviewedIdx !== -1 ? firstUnviewedIdx : 0;
          setShowStoriesViewer(true, accessible, startIndex);
        }
      }
    } catch (err) {
      console.error("Failed to fetch stories", err);
      toast.error("Failed to load stories");
    } finally {
      setStoriesLoading(false);
    }
  };

  // DND Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        setIsLoading(true);
        const { data } = await api.get(`${ENDPOINTS.GET_ALL_COMMUNITIES}?us30=1`);
        if (data.success) {
          setCommunities(data.communities);

          setTimeout(() => {
            const lastScreen = localStorage.getItem("lastScreen");
            if (
              lastScreen === "ChatScreen" ||
              lastScreen === "CommunityContentScreen" ||
              lastScreen === "UpcomingLiveSessionScreen" ||
              lastScreen === "WeeklyLinksScreen" ||
              lastScreen === "ReplaySessionsScreen"
            ) {
              const communityIndex = parseInt(
                localStorage.getItem("communityIndex"),
                10,
              );

              if (!isNaN(communityIndex)) {
                setCommunityIndex(communityIndex);
              }
            }
          }, 500);
        }
      } catch (err) {
        console.error("Failed to fetch communities", err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAllChannels = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_CHANNELS);
        if (data.success) {
          setAllChannels(data.channels);
        }
      } catch (err) {
        console.error("Failed to fetch all channels", err);
      }
    };

    fetchCommunities();
    fetchAllChannels();
  }, [setCommunities, setAllChannels]);

  // Debounce unread counts
  useEffect(() => {
    const handler = setTimeout(() => {
      const grouped = allChannels.reduce((acc, channel) => {
        const communityName = channel.community;
        const count = unreadChatCounts[channel.id] || 0;
        if (!acc[communityName]) acc[communityName] = 0;
        acc[communityName] += count;
        return acc;
      }, {});
      setDebouncedCounts(grouped);
    }, 300);

    return () => clearTimeout(handler);
  }, [unreadChatCounts, allChannels]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = communities.findIndex((c) => c.id === active.id);
    const newIndex = communities.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedCommunities = arrayMove(communities, oldIndex, newIndex);
    setCommunities(reorderedCommunities);

    try {
      const orders = reorderedCommunities.map((item, index) => ({
        id: item.id,
        sort_order: index,
      }));

      await api.post(ENDPOINTS.REORDER_COMMUNITIES, { orders });
    } catch (err) {
      console.error("Failed to reorder communities", err);
      const { data } = await api.get(`${ENDPOINTS.GET_ALL_COMMUNITIES}?us30=1`);
      if (data.success) {
        setCommunities(data.communities);
      }
    }
  };

  return (
    <div className={styles.sidebar}>
      {/* Home button - First item */}
      <SidebarItem
        active={isHomeScreen}
        onClick={() => {
          localStorage.removeItem("lastScreen");
          setIsHomeScreen();
        }}
      >
        <FaHome />
      </SidebarItem>

      {/* Admin/Trader menu icon */}
      {user?.roles[0] === "administrator" && (
        <>
          <SidebarItem
            active={isIconChoosed}
            onClick={() => {
              localStorage.removeItem("lastScreen");
              setIsIconChoosed();
            }}
          >
            <FaBars />
          </SidebarItem>

          {/* Reorder toggle button - Admin only */}
          <button
            className={styles.reorderButton}
            onClick={() => setIsReorderMode(!isReorderMode)}
            title={isReorderMode ? "Done reordering" : "Reorder communities"}
          >
            {isReorderMode ? "✓" : "⋮⋮"}
          </button>
        </>
      )}

      {user?.roles[0] === "trader" && (
        <SidebarItem
          active={isIconChoosed}
          onClick={() => {
            localStorage.removeItem("lastScreen");
            setIsIconChoosed();
          }}
        >
          <FaPlus />
        </SidebarItem>
      )}

      {/* Stories icon — visible to all users */}
      <div className={styles.storiesIconWrapper} onClick={handleStoriesClick}>
        {storiesLoading ? (
          <span style={{ fontSize: 14 }}>…</span>
        ) : (
          <img
            src="/logo512.png"
            alt="Stories"
            className={`${styles.storiesLogoIcon} ${allViewed ? styles.storiesLogoIconViewed : ""}`}
            draggable={false}
          />
        )}
        <span className={styles.storiesLabel}>Stories</span>
      </div>

      {/* Communities with drag and drop or loading skeletons */}
      {isLoading ? (
        <>
          <CommunitySkeleton />
          <CommunitySkeleton />
          <CommunitySkeleton />
          <CommunitySkeleton />
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={communities.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
            disabled={!isReorderMode}
          >
            {communities.map((community, index) => (
              <SortableCommunityItem
                key={community.id}
                community={community}
                index={index}
                active={
                  community.is_us30 ? isUS30Choosed : communityIndex === index
                }
                onClick={() => {
                  localStorage.removeItem("lastScreen");
                  if (community.is_us30) {
                    setIsUS30Choosed();
                  } else {
                    setCommunityIndex(index);
                  }
                }}
                unreadCount={debouncedCounts[community.name] || 0}
                isReorderMode={isReorderMode}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default Sidebar;
